import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { PieceStatus } from "features/builder/types";
import { ProjectUser } from "features/creator/elements";
import { MockPiece, PieceListContext, PieceCardAction } from "features/builder/pieceList";
import {
  getCardVariant,
  CardButton,
  StatusBadge,
  PieceStatusBadges,
} from "features/builder/elements/MapCard";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/builder_default_display.webp`;
const DEFAULT_USER_ICON = `${Constants.assetHost}/assets/users/icons/icon_default_kage01-1.png`;

// 1000 以上は 1.2k 表記
const formatCount = (n: number): string => {
  if (n < 1000) return String(n);
  const v = Math.floor(n / 100) / 10;
  return `${v}k`;
};

export const MapListItem = ({
  id,
  piece,
  context,
  onAction,
}: {
  // ガイドのスポットライト対象にするときだけ渡す
  id?: string;
  piece: MockPiece;
  context: PieceListContext;
  onAction: (action: PieceCardAction) => void;
}) => {
  const play = usePlaySound();
  const variant = getCardVariant(piece, context);

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  const handleCard = () => fire(PieceCardAction.DETAIL);

  const handleButton =
    (action: PieceCardAction, disabled?: boolean) => (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (disabled) return;
      fire(action);
    };

  const isEditing =
    context === PieceListContext.MINE &&
    (piece.status === PieceStatus.DRAFT_NEW || piece.status === PieceStatus.EDITING);
  const viewed = isEditing ? 0 : piece.viewed;
  const liked = isEditing ? 0 : piece.liked;
  const played = isEditing ? 0 : piece.played;

  const renderButton = (btn: CardButton) => (
    <button
      type="button"
      disabled={btn.disabled}
      onClick={handleButton(btn.action, btn.disabled)}
      aria-label={I18n.t(btn.labelKey)}
      className={`type-inherit-font btn ${btn.className} flex-col-center shrink-0 !w-16 !h-16 !p-0`}
    >
      <span className="material-symbols-outlined text-white type-title">{btn.icon}</span>
    </button>
  );

  return (
    <div
      id={id}
      onClick={handleCard}
      className="flex-row-el relative w-full pointer-events-auto cursor-pointer"
    >
      <div
        className={`flex-row-el w-full items-stretch overflow-hidden rounded-xl bg-white border-solid shadow-md ${variant.borderClass} ${variant.cardOpacityClass}`}
      >
        <div className="flex-col-el relative shrink-0 w-32 aspect-[4/3] overflow-hidden bg-beige-60">
          <img
            alt={piece.name}
            src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
            className={`w-full h-full object-cover ${variant.imageFilterClass}`}
          />
        </div>

        <div className="flex-col-el flex-1 min-w-0 items-start justify-center px-3 py-2 gap-1">
          <p className="type-text type-body text-textcolor-black !text-left truncate w-full">
            {piece.name}
          </p>
          <div className="flex-row-el flex-wrap items-center w-full gap-2">
            {context === PieceListContext.PUBLIC ? (
              variant.statusPill && (
                <StatusBadge
                  icon={variant.statusPill.icon}
                  labelKey={variant.statusPill.labelKey}
                  pillClass={variant.statusPill.pillClass}
                  textClass="text-white"
                  iconOnly
                />
              )
            ) : (
              <PieceStatusBadges piece={piece} iconOnly />
            )}
          </div>

          {variant.showMeta && (
            <div className="flex-row-el items-center gap-3">
              <div className="flex-row-el items-center gap-1">
                <span className="material-symbols-outlined text-[#4c9df1] type-sm">visibility</span>
                <p className="type-text type-sm text-textcolor-black">{formatCount(viewed)}</p>
              </div>
              <div className="flex-row-el items-center gap-1">
                <span className="material-symbols-outlined material-symbols-filled text-[#fc6560] type-sm">
                  favorite
                </span>
                <p className="type-text type-sm text-textcolor-black">{formatCount(liked)}</p>
              </div>
              <div className="flex-row-el items-center gap-1">
                <span className="material-symbols-outlined text-green-100 type-sm">play_arrow</span>
                <p className="type-text type-sm text-textcolor-black">{formatCount(played)}</p>
              </div>
            </div>
          )}
        </div>

        {piece.author_name && (
          <div className="flex-col-center shrink-0 px-2">
            <ProjectUser name={piece.author_name} icon={piece.author_icon ?? DEFAULT_USER_ICON} />
          </div>
        )}

        <div className="flex-row-el shrink-0 items-center justify-end gap-2 px-3 py-2 upsilon">
          {variant.publish && renderButton(variant.publish)}
          {variant.left && renderButton(variant.left)}
          {variant.right && renderButton(variant.right)}
          {context !== PieceListContext.TRASH && (
            <button
              type="button"
              onClick={handleButton(PieceCardAction.DETAIL)}
              aria-label={I18n.t("MSG_BUILDER_CARD_DETAIL_BTN")}
              className="type-inherit-font btn btn-primary-outlined flex-col-center shrink-0 !w-16 !h-16 !p-0 !border-2 !border-solid !border-green-100"
            >
              <span className="material-symbols-outlined text-green-100 type-title">read_more</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
